import React from 'react';
import styled, {useTheme} from 'styled-components';
import {Gauge} from '@/web-components/core/gauge/Gauge';
import {EmptyState} from '../../tickets/components/EmptyState';
import {gaugeArcColor} from './dashboardChartColors';
import type {GraphColorTokens} from './dashboardChartColors';

export interface SlaGaugeData {
	achieved: number;
	breached: number;
	total: number;
	ratePct: number | null;
	targetPct: number;
}

interface Props {
	gauge: SlaGaugeData;
}

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	height: 100%;
`;

// Gauge sweeps -145°…145° by default (very nearly a full circle). The PRD (§6.4)
// asks for a half-circle, which is this quarter-turn either side of vertical.
const HALF_CIRCLE_START_ANGLE = -90;
const HALF_CIRCLE_END_ANGLE = 90;

export function SlaAchievementGauge({gauge}: Props) {
	const {ratePct, targetPct} = gauge;
	const arcColor = gaugeArcColor(useTheme() as GraphColorTokens);

	if (ratePct === null) {
		return (
			<Wrapper>
				<EmptyState variant="inset">No tickets resolved in this period</EmptyState>
			</Wrapper>
		);
	}

	const value = Math.round(ratePct);

	return (
		<Wrapper>
			{/* Gauge renders the PRD's legend itself: a dashed swatch labelled with
			    `targetContent`, and a solid swatch in `activeColor` labelled `valueText`. */}
			<Gauge
				value={value}
				target={targetPct}
				targetContent={`vs ${targetPct}% target`}
				valueText="Actual"
				affix="%"
				width={300}
				startAngle={HALF_CIRCLE_START_ANGLE}
				endAngle={HALF_CIRCLE_END_ANGLE}
				activeColor={arcColor}
			/>
		</Wrapper>
	);
}
